import React, { useEffect } from 'react';
import { Modal, Stack, Text, Button, Group, Badge, Box, Title } from '@mantine/core';
import { ExamenEnProgreso } from '../types/examen.types';
import { useSidebar } from '../contexts/SidebarContext';

interface RecuperarExamenModalProps {
  examen: ExamenEnProgreso | null;
  opened: boolean;
  onContinuar: () => void;
  onNuevo: () => void;
  loading?: boolean;
  onOpen?: () => void;
}

const RecuperarExamenModal: React.FC<RecuperarExamenModalProps> = ({
  examen,
  opened,
  onContinuar,
  onNuevo,
  loading = false,
  onOpen
}) => {
  const { setCollapsed } = useSidebar();
  
  useEffect(() => {
    if (opened) {
      setCollapsed(true);
      if (onOpen) {
        onOpen();
      }
    }
    // Solo ejecutar cuando cambie 'opened', no cuando cambien las funciones
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);
  console.log('🔎 [MODAL] Renderizando RecuperarExamenModal');
  console.log('📦 [MODAL] Props:', {
    examen: examen ? {
      intentoId: examen.intentoId,
      tipo: examen.tipoExamen,
      nombre: examen.nombreExamen
    } : null,
    opened,
    loading
  });

  if (!examen) {
    console.log('⚠️ [MODAL] No hay examen para mostrar, retornando null');
    return null;
  }

  const formatearTiempo = (segundos: number): string => {
    const horas = Math.floor(segundos / 3600);
    const mins = Math.floor((segundos % 3600) / 60);
    const secs = segundos % 60;

    if (horas > 0) {
      return `${horas}h ${mins}m ${secs}s`;
    }
    return `${mins}m ${secs}s`;
  };

  const formatearFecha = (fecha: string): string => {
    const date = new Date(fecha);
    const ahora = new Date();
    const diff = ahora.getTime() - date.getTime();
    const horas = Math.floor(diff / (1000 * 60 * 60));

    if (horas < 1) {
      const minutos = Math.floor(diff / (1000 * 60));
      return `Hace ${minutos} minuto${minutos !== 1 ? 's' : ''}`;
    }
    if (horas < 24) {
      return `Hace ${horas} hora${horas !== 1 ? 's' : ''}`;
    }
    const dias = Math.floor(horas / 24);
    return `Hace ${dias} día${dias !== 1 ? 's' : ''}`;
  };

  // Verificar si el examen está 100% completado
  const estaCompletado = (examen.preguntaActual + 1) >= examen.totalPreguntas;
  
  // Detectar intento corrupto: pregunta 0 o 1 con tiempo 0
  const esIntentoCorrupto = (examen.preguntaActual <= 0 && examen.tiempoTranscurrido === 0);

  return (
    <Modal
      opened={opened}
      onClose={() => {}} // No permitir cerrar sin elegir
      centered
      size="sm"
      radius="lg"
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
      overlayProps={{ blur: 3 }}
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <Stack gap="lg">
        <Box>
          <Title order={4} mb="xs">
            {esIntentoCorrupto ? 'Examen Corrupto Detectado' : estaCompletado ? 'Examen Completado' : 'Examen en Progreso'}
          </Title>
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Text size="sm" fw={600}>{examen.nombreExamen}</Text>
              <Badge size="sm" radius="sm" variant="light">{examen.tipoExamen}</Badge>
            </Group>

            <Text size="xs" c="dimmed">
              {formatearFecha(examen.iniciadoEn)}
            </Text>

            <Stack gap={4}>
              <Group justify="space-between">
                <Text size="xs">Progreso</Text>
                <Text size="xs" fw={600}>
                  {examen.preguntaActual + 1} / {examen.totalPreguntas}
                </Text>
              </Group>

              <Group justify="space-between">
                <Text size="xs">Tiempo transcurrido</Text>
                <Text size="xs" fw={600}>{formatearTiempo(examen.tiempoTranscurrido)}</Text>
              </Group>

              {examen.pausado && (
                <Group justify="space-between">
                  <Text size="xs">Estado</Text>
                  <Badge size="sm" color="orange" variant="light">Pausado</Badge>
                </Group>
              )}
            </Stack>

            <Text size="xs" c="dimmed" mt="xs">
              {esIntentoCorrupto
                ? 'Este examen parece estar corrupto (datos inconsistentes). Se recomienda comenzar uno nuevo.'
                : estaCompletado
                ? 'Has respondido todas las preguntas. Puedes finalizar el examen para ver tus resultados o comenzar uno nuevo.'
                : '¿Deseas continuar donde lo dejaste o comenzar uno nuevo?'
              }
            </Text>
          </Stack>
        </Box>
        <Group justify="flex-end" gap="sm">
          <Button
            variant="default"
            onClick={() => {
              console.log('🔘 [MODAL] Usuario hizo clic en "Comenzar Nuevo"');
              console.log('📦 [MODAL] Llamando onNuevo()');
              onNuevo();
            }}
            disabled={loading}
            radius="md"
          >
            Comenzar Nuevo
          </Button>
          {!esIntentoCorrupto && (
            <Button
              variant="filled"
              color="dark"
              onClick={() => {
                console.log(`🔘 [MODAL] Usuario hizo clic en "${estaCompletado ? 'Finalizar' : 'Continuar'} Examen"`);
                console.log('📦 [MODAL] Llamando onContinuar()');
                onContinuar();
              }}
              loading={loading}
              radius="md"
            >
              {estaCompletado ? 'Finalizar Examen' : 'Continuar Examen'}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
};

export default RecuperarExamenModal;
